import { useState, useEffect } from "react";
import "./ProductQuickView.css";
import settingsService from "../../services/settingsService";

const ProductQuickView = ({ product, onClose, onAddToCart }) => {
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [sizesWithLabels, setSizesWithLabels] = useState([]);
  const [loadingSizes, setLoadingSizes] = useState(true);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?product=${product._id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} on Kitty N Mice!`,
          url: shareUrl,
        });
      } catch (err) {
        // user cancelled, do nothing
      }
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Fetch size labels from settings
  useEffect(() => {
    const fetchSizeLabels = async () => {
      try {
        setLoadingSizes(true);
        const response = await settingsService.getSizes();

        if (response?.success && response?.data) {
          const sizeSettings = response.data;

          // Map product sizes to their labels and sort by index
          if (product.sizes && product.sizes.length > 0) {
            const mappedSizes = product.sizes.map((sizeValue) => {
              const sizeInfo = sizeSettings.find(
                (s) => s.value === sizeValue && s.isActive
              );
              return {
                value: sizeValue,
                label: sizeInfo?.label || sizeValue,
                index: sizeInfo?.index || 0,
              };
            });
            // Sort by index for hierarchical display
            mappedSizes.sort((a, b) => a.index - b.index);
            setSizesWithLabels(mappedSizes);
          }
        }
      } catch (error) {
        console.error("Error fetching size labels:", error);
        // Fallback to using sizes as is
        if (product.sizes) {
          setSizesWithLabels(
            product.sizes.map((size) => ({ value: size, label: size }))
          );
        }
      } finally {
        setLoadingSizes(false);
      }
    };

    if (product) {
      fetchSizeLabels();
    }
  }, [product]);

  if (!product) return null;

  const handleAddToCart = () => {
    if (selectedSize) {
      onAddToCart({ ...product, selectedSize, quantity });
      onClose();
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === product.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1
    );
  };

  const formatPrice = (price) => {
    return `₹${price?.toLocaleString("en-IN")}`;
  };

  return (
    <div className="quick-view-overlay-new" onClick={onClose}>
      <div
        className="quick-view-modal-new"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile sticky top bar with drag handle + share + close */}
        <div className="qv-mobile-topbar">
          <div className="qv-drag-handle" />
          <button className="qv-mobile-share-btn" onClick={handleShare}>
            {shareCopied ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            )}
          </button>
          <button className="qv-mobile-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Desktop close + share buttons */}
        <div className="qv-desktop-actions">
          <button className="modal-share-btn-new" onClick={handleShare} title="Share product">
            {shareCopied ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            )}
            <span className="share-tooltip">{shareCopied ? "Copied!" : "Share"}</span>
          </button>
          <button className="modal-close-btn-new" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="quick-view-content-new">
          {/* Image Section */}
          <div className="quick-view-left-new">
            <div className="image-carousel-new">
              <img
                src={product.images[currentImageIndex]?.url}
                alt={product.name}
                className="main-product-image-new"
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/600x800?text=No+Image";
                }}
              />
              {product.images.length > 1 && (
                <>
                  <button
                    className="image-nav-new prev-img-new"
                    onClick={prevImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    className="image-nav-new next-img-new"
                    onClick={nextImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </>
              )}

              {/* Image Counter */}
              {product.images.length > 1 && (
                <div className="image-counter-new">
                  {currentImageIndex + 1} / {product.images.length}
                </div>
              )}

              {/* Badges */}
              <div className="quick-view-badges-new">
                {product.isNew && (
                  <span className="qv-badge-new qv-badge-new-product">New</span>
                )}
                {product.isHotDeal && (
                  <span className="qv-badge-new qv-badge-hot-deal">
                    Hot Deal 🔥
                  </span>
                )}
                {product.isFeatured && (
                  <span className="qv-badge-new qv-badge-featured">
                    Featured
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnail Images */}
            {product.images.length > 1 && (
              <div className="image-thumbnails-new">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    className={`thumbnail-new ${
                      index === currentImageIndex ? "active" : ""
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={img?.url}
                      alt={`${product.name} ${index + 1}`}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/100x100?text=No+Image";
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details Section */}
          <div className="quick-view-right-new">
            {/* Category */}
            {product.category && (
              <div className="qv-category-new">
                <span>{product.category.name}</span>
              </div>
            )}

            {/* Title */}
            <h2 className="quick-view-title-new">{product.name}</h2>

            {/* Price */}
            <div className="quick-view-price-section-new">
              {product.offerPrice && product.offerPrice < product.price ? (
                <>
                  <span className="qv-price-current-new">
                    {formatPrice(product.offerPrice)}
                  </span>
                  <span className="qv-price-compare-new">
                    {formatPrice(product.price)}
                  </span>
                  <span className="qv-discount-badge-new">
                    {Math.round(
                      ((product.price - product.offerPrice) / product.price) *
                        100
                    )}
                    % OFF
                  </span>
                </>
              ) : (
                <span className="qv-price-current-new">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>

            {/* Rating (Optional - if you have ratings) */}
            {product.ratings && product.ratings.count > 0 && (
              <div className="qv-rating-new">
                <div className="stars-new">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={
                        i < Math.floor(product.ratings.average)
                          ? "star-filled"
                          : "star-empty"
                      }
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="rating-count-new">
                  ({product.ratings.count} reviews)
                </span>
              </div>
            )}

            {/* Description */}
            <div className="product-description-new">
              <p>{product.description || "No description available."}</p>
            </div>

            {/* Stock Status */}
            <div className="stock-status-new">
              {product.stock > 0 ? (
                product.stock <= 5 ? (
                  <span className="stock-low-new">
                    ⚠️ Only {product.stock} left in stock!
                  </span>
                ) : (
                  <span className="stock-available-new">✓ In Stock</span>
                )
              ) : (
                <span className="stock-out-new">✗ Out of Stock</span>
              )}
            </div>

            {/* Size Selector */}
            {sizesWithLabels.length > 0 && (
              <div className="size-selector-section-new">
                <div className="selector-header-new">
                  <label className="selector-label-new">Select Size</label>
                  {selectedSize && (
                    <span className="selected-indicator-new">
                      Selected:{" "}
                      {sizesWithLabels.find((s) => s.value === selectedSize)
                        ?.label || selectedSize}
                    </span>
                  )}
                </div>
                {loadingSizes ? (
                  <div className="size-loading-new">Loading sizes...</div>
                ) : (
                  <div className="size-options-new">
                    {sizesWithLabels.map((size) => (
                      <button
                        key={size.value}
                        className={`size-option-new ${
                          selectedSize === size.value ? "selected" : ""
                        }`}
                        onClick={() => setSelectedSize(size.value)}
                        title={size.label}
                      >
                        <span className="size-label">{size.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Quantity Selector */}
            <div className="quantity-selector-section-new">
              <label className="selector-label-new">Quantity</label>
              <div className="quantity-controls-new">
                <button
                  className="qty-btn-new"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
                <span className="qty-display-new">{quantity}</span>
                <button
                  className="qty-btn-new"
                  onClick={() =>
                    setQuantity(Math.min(product.stock, quantity + 1))
                  }
                  disabled={quantity >= product.stock}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              className={`add-cart-btn-new ${
                !selectedSize || product.stock === 0 ? "disabled" : ""
              }`}
              onClick={handleAddToCart}
              disabled={!selectedSize || product.stock === 0}
            >
              {product.stock === 0
                ? "Out of Stock"
                : selectedSize
                ? "Add to Cart"
                : "Please Select Size"}
            </button>

            {/* Product Info Tags */}
            <div className="product-info-tags-new">
              <div className="info-tag-new">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>Free Shipping on orders above ₹999</span>
              </div>
              <div className="info-tag-new">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>3 day easy returns</span>
              </div>
              <div className="info-tag-new">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="1" y="3" width="15" height="13" />
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                <span>Fast delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductQuickView;
